
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { recentArticles, categories } from '../../../mocks/articles';

export default function ArticleList() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [allArticles, setAllArticles] = useState(recentArticles);

  // ローカルストレージから投稿された記事を取得する関数
  const loadArticles = () => {
    const userArticles = JSON.parse(localStorage.getItem('userArticles') || '[]');
    // 特定の記事を除外（タイトルで判定）
    const filteredUserArticles = userArticles.filter(article => 
      article.title !== 'AIとHologramが描く未来のテクノロジー最前線'
    );
    const combinedArticles = [...filteredUserArticles, ...recentArticles];
    setAllArticles(combinedArticles);
    
    // ローカルストレージも更新
    localStorage.setItem('userArticles', JSON.stringify(filteredUserArticles));
  };

  // 初回読み込み時とフォーカス時に記事を取得
  useEffect(() => {
    loadArticles();
    
    // ページにフォーカスが戻った時に記事を再読み込み
    const handleFocus = () => {
      loadArticles();
    };

    // 記事更新イベントリスナー
    const handleArticleUpdate = () => {
      loadArticles();
    };
    
    window.addEventListener('focus', handleFocus);
    window.addEventListener('articleUpdated', handleArticleUpdate);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('articleUpdated', handleArticleUpdate);
    };
  }, []);

  const filteredArticles = allArticles.filter(article => {
    const matchesCategory = selectedCategory === 'all' || 
      article.category.toLowerCase().includes(selectedCategory.toLowerCase());
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-900 text-white py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <button 
                onClick={() => navigate('/')}
                className="text-2xl font-bold hover:text-blue-400 transition-colors cursor-pointer"
              >
                AI Code グループ
              </button>
              <span className="text-blue-400 text-lg">MEDIA</span>
            </div>
            
            <nav className="hidden md:flex items-center space-x-6">
              <button 
                onClick={() => navigate('/media')}
                className="hover:text-blue-400 transition-colors cursor-pointer"
              >
                ホーム
              </button>
              <button 
                onClick={() => navigate('/media/articles')}
                className="text-blue-400 cursor-pointer"
              >
                記事一覧
              </button>
              <button 
                onClick={() => navigate('/media/post')}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                記事を投稿
              </button>
              <button 
                onClick={() => navigate('/admin/login')}
                className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                管理者
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Page Header */}
      <section className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">記事一覧</h1>
            <p className="text-xl text-gray-600">
              AI・ホログラム技術に関する最新記事をお読みいただけます
            </p>
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-8">
            <div className="relative flex-1 max-w-md">
              <input
                type="search"
                placeholder="記事を検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-gray-600 font-medium">カテゴリー:</span>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
              >
                <option value="all">すべて</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.slug}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-8">
            <p className="text-gray-600">
              {filteredArticles.length}件の記事が見つかりました
            </p>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article) => (
              <article key={article.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all transform hover:scale-105">
                <div 
                  onClick={() => navigate(`/media/articles/${article.id}`)}
                  className="cursor-pointer"
                >
                  <img 
                    src={article.mainImage || article.image}
                    alt={article.title}
                    className="w-full h-48 object-cover object-center"
                  />
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-medium">
                        {article.category}
                      </span>
                      <span className="text-gray-500 text-sm">{article.date}</span>
                    </div>
                    <h3 className="font-bold text-xl mb-3 leading-tight hover:text-blue-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>by {article.author}</span>
                      <div className="flex items-center">
                        <i className="ri-eye-line mr-1"></i>
                        <span>{article.views?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Load More Button */}
          <div className="text-center mt-12">
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full font-semibold transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap">
              さらに記事を読み込む
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="bg-blue-600 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            最新記事をメールでお届け
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            AI・ホログラム技術の最新情報を定期的にお送りします
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="メールアドレスを入力"
              className="flex-1 px-4 py-3 rounded-full focus:outline-none focus:ring-2 focus:ring-white"
            />
            <button className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap">
              購読する
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold mb-4">HOLOGRAM MEDIA</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                AIとホログラム技術の最新情報をお届けするオウンドメディアです。
                業界の最新トレンドから技術解説まで幅広くカバーしています。
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">コンテンツ</h4>
              <ul className="space-y-2 text-gray-300">
                <li><button onClick={() => navigate('/media/articles')} className="hover:text-white cursor-pointer">記事一覧</button></li>
                <li><button onClick={() => navigate('/media/post')} className="hover:text-white cursor-pointer">記事投稿</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">リンク</h4>
              <ul className="space-y-2 text-gray-300">
                <li><button onClick={() => navigate('/')} className="hover:text-white cursor-pointer">メインサイト</button></li>
                <li><a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Made with Readdy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 AI Code グループ MEDIA. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
